import React, { PropsWithChildren, ReactElement, useState } from 'react';
import { Image, Platform, ScrollView, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import {
  Disclaimer,
  DISCLAIMER_MODE,
  DNABox,
  DNAButton,
  DNAText,
  Iffy,
  InformationMessage,
  luxColors,
  Modal,
  responsive,
} from '@alucio/lux-ui';
import { useAuthenticator } from '@aws-amplify/ui-react-native';
import { isFirefox, isMobile, isSafari } from 'react-device-detect';
import { DisclaimerType } from '../Profile/Profile';

const LoginBackground = require('../../../assets/images/LoginBackground.jpg');
const BeaconIconLogo = require('../../../assets/images/Beacon_Icon_Logo.svg');
const webLegacyViewportLayout = Platform.select({
  web: {
    bottom: 0,
    height: 'auto',
    left: 0,
    marginHorizontal: 0,
    position: 'fixed',
    right: 0,
    top: 0,
  } as unknown as ViewStyle,
  default: {},
}) || {};

const styles = StyleSheet.create({
  backdrop: {
    backgroundColor: luxColors.modalBackdrop.primary,
  },
  beaconText: {
    color: luxColors.alucioPurple.primary,
    fontSize: 24,
    fontWeight: 'bold',
    paddingBottom: 20,
    paddingTop: 20,
  },
  bottomOption: {
    color: luxColors.headerTextColor.primary,
    fontSize: 13,
  },
  closeButton: {
    marginVertical: 24,
  },
  footer: {
    paddingBottom: 16,
  },
  formContent: {
    width: '100%',
  },
  formPanel: {
    backgroundColor: luxColors.info.primary,
    maxWidth: '100%',
    paddingHorizontal: 64,
    paddingTop: 100,
    width: 504,
  },
  formScrollContent: {
    flexGrow: 1,
  },
  formScrollRegion: {
    alignSelf: 'flex-end',
    flex: 1,
    maxWidth: '100%',
    width: 504,
  },
  horizontalOffset: {
    marginHorizontal: -50,
  },
  iconLogoImage: {
    height: 50,
    left: 16,
    resizeMode: 'contain',
    width: 60,
  },
  legacyBackgroundImage: {
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  legacyShell: {
    height: responsive.dimensions.trueHundredHeight,
    marginHorizontal: -16,
    position: 'relative',
    width: '100%',
    ...webLegacyViewportLayout,
  },
  modalContentText: {
    color: luxColors.headerTextColor.primary,
    fontSize: 14,
    lineHeight: 20,
  },
  modalContentWrapper: {
    paddingBottom: 65,
    paddingTop: 16,
  },
  modalFooter: {
    borderBottomWidth: 0,
  },
  modalHeader: {
    paddingBottom: 16,
  },
  modalHeaderText: {
    color: luxColors.blueText.tertiary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  modalSection: {
    borderBottomColor: luxColors.divider.tertiary,
    borderBottomWidth: 1,
    paddingHorizontal: 16,
  },
  modalWrapper: {
    backgroundColor: luxColors.info.primary,
    borderRadius: 4,
    paddingTop: 16,
    width: 500,
  },
  needHelp: {
    color: luxColors.thumbnailBorder.primary,
    fontSize: 13,
  },
  verticalDivider: {
    borderRightColor: luxColors.headerTextColor.primary,
    borderRightWidth: 1,
    height: 16,
    marginHorizontal: 16,
  },
});

interface HelpModalProps {
  toggleHelpModal: () => void;
}

interface Props {
  title?: string;
  isLogin?: boolean;
  showReturnToLogin?: boolean;
  changePasswordEnabled?: boolean;
}

const LegacyFormWrapper: React.FC<PropsWithChildren<Props>> = (props) => {
  const [isHelpModalShown, setIsHelpModalShown] = useState<boolean>(false);
  const [disclaimer, setDisclaimer] =
    useState<DisclaimerType>({ isVisble: false, mode: DISCLAIMER_MODE.Privacy });
  const { toForgotPassword, toSignIn } = useAuthenticator();

  function toggleDisplayModal(): void {
    setIsHelpModalShown((isShown) => !isShown);
  }

  function onCloseDisclaimer(): void {
    setDisclaimer({ ...disclaimer, isVisble: false });
  }

  function showPrivacy(): void {
    setDisclaimer({
      isVisble: true,
      mode: DISCLAIMER_MODE.Privacy,
    });
  }

  function showToU(): void {
    setDisclaimer({
      isVisble: true,
      mode: DISCLAIMER_MODE.ToU,
    });
  }

  return (
    <DNABox
      testID="legacy-authentication-shell"
      style={styles.legacyShell}
      appearance="col"
      alignX="end"
      fill
    >
      <Image source={LoginBackground} style={styles.legacyBackgroundImage} resizeMode="cover" />
      {disclaimer.isVisble
        ? <Disclaimer mode={disclaimer.mode} onClose={onCloseDisclaimer} />
        : null
      }
      <Iffy is={isHelpModalShown}>
        <HelpModal toggleHelpModal={toggleDisplayModal} />
      </Iffy>
      <ScrollView
        testID="legacy-authentication-scroll-region"
        style={styles.formScrollRegion}
        contentContainerStyle={styles.formScrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <DNABox
          testID="legacy-authentication-panel"
          style={styles.formPanel}
          appearance="col"
          spacing="between"
          fill
        >
          <DNABox style={styles.formContent} appearance="col" alignX="center">
            <Iffy is={isFirefox || (!isMobile && isSafari)}>
              <InformationMessage
                variance="warning"
                customWrapperStyle={styles.horizontalOffset}
                text="For the best user experience please use the latest version of the Chrome web browser."
              />
            </Iffy>
            <Image
              testID="legacy-authentication-logo"
              source={BeaconIconLogo}
              style={styles.iconLogoImage}
            />
            <Iffy is={Boolean(props.title)}>
              <DNAText style={styles.beaconText}>{props.title}</DNAText>
            </Iffy>
            {props.children}
            <DNABox testID="legacy-authentication-actions" appearance="row" alignY="center">
              <Iffy is={Boolean(props.isLogin && props.changePasswordEnabled)}>
                <TouchableOpacity testID="forgot-password" onPress={toForgotPassword}>
                  <DNAText style={styles.needHelp}>Forgot password?</DNAText>
                </TouchableOpacity>
              </Iffy>
              <Iffy is={Boolean(props.showReturnToLogin)}>
                <TouchableOpacity onPress={toSignIn}>
                  <DNAText style={styles.needHelp}>Log in</DNAText>
                </TouchableOpacity>
              </Iffy>
              <TouchableOpacity onPress={toggleDisplayModal}>
                <DNAText style={[styles.needHelp, { marginLeft: 20 }]}>Need help?</DNAText>
              </TouchableOpacity>
            </DNABox>
          </DNABox>
          <DNABox
            testID="legacy-authentication-footer"
            style={styles.footer}
            appearance="row"
            alignX="center"
            alignY="center"
          >
            <TouchableOpacity onPress={showToU}>
              <DNAText style={styles.bottomOption}>Terms of Use</DNAText>
            </TouchableOpacity>
            <DNABox style={styles.verticalDivider} />
            <TouchableOpacity onPress={showPrivacy}>
              <DNAText style={styles.bottomOption}>Privacy Policy</DNAText>
            </TouchableOpacity>
          </DNABox>
        </DNABox>
      </ScrollView>
    </DNABox>
  );
};

const HelpModal = (props: HelpModalProps): ReactElement => {
  const { toggleHelpModal } = props;

  return (
    <Modal visible={true} onBackdropPress={toggleHelpModal} backdropStyle={styles.backdrop}>
      <DNABox style={styles.modalWrapper} appearance="col">
        <DNABox
          style={[styles.modalSection, styles.modalHeader]}
          appearance="row"
          alignY="center"
          spacing="between"
        >
          <DNAText h5 bold style={styles.modalHeaderText}>Need help?</DNAText>
          <DNAButton iconLeft="x" appearance="ghost" status="subtle" onPress={toggleHelpModal} />
        </DNABox>
        <DNABox
          style={[styles.modalSection, styles.modalContentWrapper]}
          appearance="row"
          wrap="stretch"
          fill
        >
          <DNAText style={styles.modalContentText}>
            Please contact Alucio Support
            <DNAText bold> (support@alucio.io)</DNAText>
            {' for assistance with your account.'}
          </DNAText>
        </DNABox>
        <DNABox
          style={[styles.modalSection, styles.modalFooter]}
          appearance="row"
          alignX="end"
        >
          <DNAButton style={styles.closeButton} onPress={toggleHelpModal}>
            Close
          </DNAButton>
        </DNABox>
      </DNABox>
    </Modal>
  );
};

export default LegacyFormWrapper;
